import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  bulkUpsertAdminWarehouseLookupEntries,
  createAdminWarehouseLookupEntry,
  deleteAdminWarehouseLookupEntry,
  getWarehouseLookupEntryUsage,
  listAdminWarehouseLookupEntries,
  listAdminWarehouseLookupTables,
  updateAdminWarehouseLookupEntry,
} from '@/api/adminWarehouseLookups'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type {
  PaginatedWarehouseLookupEntriesResponse,
  WarehouseLookupBulkUpsertPayload,
  WarehouseLookupCreatePayload,
  WarehouseLookupEntry,
  WarehouseLookupEntryUsage,
  WarehouseLookupListParams,
  WarehouseLookupTableName,
  WarehouseLookupTableSummary,
  WarehouseLookupUpdatePayload,
} from '@/types/adminWarehouseLookups'

interface CrudMutationArgs {
  tableName: WarehouseLookupTableName
}

interface CreateMutationArgs extends CrudMutationArgs {
  payload: WarehouseLookupCreatePayload
}

interface UpdateMutationArgs extends CrudMutationArgs {
  id: string
  payload: WarehouseLookupUpdatePayload
}

interface DeleteMutationArgs extends CrudMutationArgs {
  id: string
}

interface BulkMutationArgs extends CrudMutationArgs {
  payload: WarehouseLookupBulkUpsertPayload
}

function invalidateWarehouseLookupQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  tableName: WarehouseLookupTableName
) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.adminWarehouseLookups.all,
  })
  queryClient.invalidateQueries({
    queryKey: queryKeys.adminWarehouseLookups.entriesPrefix(tableName),
  })
}

export function useAdminWarehouseLookupTables() {
  return useQuery<WarehouseLookupTableSummary[]>({
    queryKey: queryKeys.adminWarehouseLookups.tables(),
    queryFn: listAdminWarehouseLookupTables,
  })
}

export function useAdminWarehouseLookupEntries({
  tableName,
  params,
}: {
  tableName: WarehouseLookupTableName | null
  params: WarehouseLookupListParams
}) {
  const paramsKey: Record<string, unknown> = {
    skip: params.skip ?? 0,
    limit: params.limit ?? 20,
    search: params.search ?? '',
  }

  return useQuery<PaginatedWarehouseLookupEntriesResponse>({
    queryKey: queryKeys.adminWarehouseLookups.entries(
      tableName ?? 'none',
      paramsKey
    ),
    queryFn: () => {
      if (!tableName) {
        return Promise.resolve({
          data: [],
          pagination: {
            skip: 0,
            limit: params.limit ?? 20,
            total: 0,
            has_more: false,
          },
        })
      }

      return listAdminWarehouseLookupEntries(tableName, params)
    },
    enabled: !!tableName,
  })
}

export function useCreateAdminWarehouseLookupEntry() {
  const queryClient = useQueryClient()

  return useMutation<WarehouseLookupEntry, Error, CreateMutationArgs>({
    mutationFn: ({ tableName, payload }) =>
      createAdminWarehouseLookupEntry(tableName, payload),
    onSuccess: (_, variables) => {
      invalidateWarehouseLookupQueries(queryClient, variables.tableName)
      toast.success('Warehouse lookup entry created')
    },
    onError: error => {
      toast.error(
        getErrorMessage(error, 'Failed to create warehouse lookup entry')
      )
    },
  })
}

export function useUpdateAdminWarehouseLookupEntry() {
  const queryClient = useQueryClient()

  return useMutation<WarehouseLookupEntry, Error, UpdateMutationArgs>({
    mutationFn: ({ tableName, id, payload }) =>
      updateAdminWarehouseLookupEntry(tableName, id, payload),
    onSuccess: (_, variables) => {
      invalidateWarehouseLookupQueries(queryClient, variables.tableName)
      toast.success('Warehouse lookup entry updated')
    },
    onError: error => {
      toast.error(
        getErrorMessage(error, 'Failed to update warehouse lookup entry')
      )
    },
  })
}

export function useDeleteAdminWarehouseLookupEntry() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteMutationArgs>({
    mutationFn: ({ tableName, id }) =>
      deleteAdminWarehouseLookupEntry(tableName, id),
    onSuccess: (_, variables) => {
      invalidateWarehouseLookupQueries(queryClient, variables.tableName)
      toast.success('Warehouse lookup entry deactivated')
    },
    onError: error => {
      toast.error(
        getErrorMessage(error, 'Failed to delete warehouse lookup entry')
      )
    },
  })
}

export function useBulkUpsertAdminWarehouseLookupEntries() {
  const queryClient = useQueryClient()

  return useMutation<WarehouseLookupEntry[], Error, BulkMutationArgs>({
    mutationFn: ({ tableName, payload }) =>
      bulkUpsertAdminWarehouseLookupEntries(tableName, payload),
    onSuccess: (results, variables) => {
      invalidateWarehouseLookupQueries(queryClient, variables.tableName)
      toast.success(`Imported ${results.length} warehouse lookup entries`)
    },
    onError: error => {
      toast.error(
        getErrorMessage(error, 'Failed to import warehouse lookup entries')
      )
    },
  })
}

export function useWarehouseLookupEntryUsage(
  tableName: WarehouseLookupTableName | null,
  lookupKey: string | null
) {
  return useQuery<WarehouseLookupEntryUsage>({
    queryKey: queryKeys.adminWarehouseLookups.usage(
      tableName ?? '',
      lookupKey ?? ''
    ),
    queryFn: () => getWarehouseLookupEntryUsage(tableName!, lookupKey!),
    enabled: !!tableName && !!lookupKey,
  })
}
